import React, { useCallback, useEffect, useState } from 'react';
import { endHostLink, getHostLink, HostLink, requestHostLink } from '../../../../api/building-links-client';
import { StaysApiError } from '../../../../api/stays-client';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { useAuth } from '../../../../context/auth-provider';
import { formatDate } from '../../../../function-utils/locale';
import toast from '../../../../utils/toast';
import { AppButton } from '../../../button/app-button';
import { Field, FieldError, FieldHint, Input, Label } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { StatusPill } from '../../../status-pill/status-pill';
import { ActionRow, Card, CardTitle } from '../../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { ERROR_COPY } from '../../dashboard/dashboard-stays-components/stays-labels';

/**
 * FRONT DESK — the host's side of the building link (Independent Stays plan IS7).
 *
 * For a place in a building whose front desk runs on Lobbero under another account. The
 * host enters the building's code and names the apartment; the building picks the unit
 * and approves. The host sees the building's name and city and nothing else of theirs.
 * Only an owner or manager connects or ends it (it shares guests with another company);
 * a co-host sees where it stands.
 *
 * The one place a Stays-only host reads "front desk": it is the building's desk, named
 * as the host would (the plan's wording, an agreed exception to R-ISC.2).
 *
 * The caller mounts it only with the switch on and stays:manage.
 */

const dayOf = (at?: string | null): string =>
  at ? formatDate(at, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const wordsOf = (e: unknown, fallback: string): string => {
  if (e instanceof StaysApiError && e.code && (ERROR_COPY as Record<string, string>)[e.code]) {
    return (ERROR_COPY as Record<string, string>)[e.code];
  }

  return e instanceof Error && e.message ? e.message : fallback;
};

export const ListingBuildingLinkCard: React.FC<{ listingId: string }> = ({ listingId }) => {
  const { permissions }: { permissions: string[] } = useAuth();
  const canChange = can(permissions, PERMISSIONS.STAYS_PROMOTE);
  const [link, setLink] = useState<HostLink | null>(null);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ending, setEnding] = useState(false);

  const read = useCallback(() => {
    getHostLink(listingId)
      .then(setLink)
      .catch(() => setLink(null));
  }, [listingId]);

  useEffect(() => {
    read();
  }, [read]);

  if (!link) return null;

  const connect = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setFields({});
    try {
      setLink(await requestHostLink(listingId, { code: code.trim(), apartment_label: label.trim() }));
      setCode('');
      setLabel('');
    } catch (e) {
      if (e instanceof StaysApiError && e.status === 400) {
        setFields(e.fields);
      } else {
        setProblem(wordsOf(e, 'Could not send the request.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    setEnding(false);
    if (busy) return;
    setBusy(true);
    try {
      setLink(await endHostLink(listingId));
    } catch (e) {
      toast.error(wordsOf(e, 'Could not end the link.'));
      read();
    } finally {
      setBusy(false);
    }
  };

  const live = link.status === 'requested' || link.status === 'approved';

  return (
    <Card aria-labelledby="stays-front-desk-title" data-testid="listing-building-link" data-link-status={link.status}>
      <CardTitle id="stays-front-desk-title">Front desk</CardTitle>

      {link.status === 'requested' ? (
        <p>
          <StatusPill tone="warning" dot>
            Waiting for the building
          </StatusPill>{' '}
          {`You asked ${link.building_name} to link apartment "${link.apartment_label}".`}
        </p>
      ) : null}

      {link.status === 'approved' ? (
        <>
          <p>
            <StatusPill tone="success" dot>
              Linked
            </StatusPill>{' '}
            {`${link.building_name}${link.city ? `, ${link.city}` : ''}, since `}
            <span data-at={link.decided_at || undefined}>{dayOf(link.decided_at)}</span>.
          </p>
          {link.desk_available === false ? (
            <p role="status">This building&apos;s desk is not taking guests right now.</p>
          ) : (
            <p>
              Guests agree to share their name, arrival date, party size and ID status with the desk during check-in. The
              desk then signs them in, and you are told when they arrive.
            </p>
          )}
        </>
      ) : null}

      {link.status === 'refused' ? <p role="status">{`${link.building_name} did not approve the link.`}</p> : null}
      {link.status === 'ended' ? (
        <p role="status">
          {`${link.building_name}: the link ended on `}
          <span data-at={link.ended_at || undefined}>{dayOf(link.ended_at)}</span>.
        </p>
      ) : null}

      {!live && canChange ? (
        <>
          <p>
            If this place is in a building with a front desk on Lobbero, the building can give you a code. Its desk then
            expects your guests.
          </p>
          <Field>
            <Label htmlFor="link-code">Code from the building</Label>
            <Input id="link-code" value={code} autoComplete="off" onChange={e => setCode(e.target.value)} />
            <FieldError message={fields.code} />
          </Field>
          <Field>
            <Label htmlFor="link-apartment">Your apartment</Label>
            <Input id="link-apartment" value={label} maxLength={24} onChange={e => setLabel(e.target.value)} />
            <FieldHint>As the building knows it, for example 4B.</FieldHint>
            <FieldError message={fields.apartment_label} />
          </Field>
          {problem ? <p role="alert">{problem}</p> : null}
          <ActionRow>
            <AppButton disabled={busy || !code.trim() || !label.trim()} onClick={connect}>
              Connect to the front desk
            </AppButton>
          </ActionRow>
        </>
      ) : null}

      {!live && !canChange && link.status === 'none' ? <p>This place is not linked to a building&apos;s front desk.</p> : null}

      {live && canChange ? (
        <ActionRow>
          <AppButton variant="ghost" disabled={busy} onClick={() => setEnding(true)}>
            End link
          </AppButton>
        </ActionRow>
      ) : null}

      <ConfirmDialog
        open={ending}
        title="End this link?"
        message="The desk stops seeing your guests at once. Your stays are not changed."
        primaryLabel="End link"
        primaryVariant="destructive"
        dismissLabel="Keep the link"
        onDismiss={() => setEnding(false)}
        onPrimary={() => {
          void end();
        }}
      />
    </Card>
  );
};
